/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

/* eslint-disable camelcase */
import React from 'react';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import { Icon, CustomValidation, Button, FlexRow, FlexCol } from 'sarsaparilla';

const propTypes = {
    index: PropTypes.number,
    removeLeader: PropTypes.func,
    leader: PropTypes.shape({
        first_name: PropTypes.string,
        email: PropTypes.string,
        cell_phone: PropTypes.string,
        last_name: PropTypes.string,
    }),
    className: PropTypes.string,
    alternateLeaderValidatorMethod: PropTypes.func.isRequired,
    isDisabled: PropTypes.bool,
    pActions: PropTypes.shape().isRequired,
    reservationId: PropTypes.string,
};

const defaultProps = {
    isDisabled: false,
    className: '',
};

export default function AlternatePermitHolderNamePhoneEntry({
    index,
    removeLeader,
    pActions,
    reservationId,
    isDisabled,
    leader,
    className,
    alternateLeaderValidatorMethod,
}) {
    const onClickRemove = () => {
        const indexNumber = parseInt(index, 10);
        removeLeader(indexNumber);
    };

    const handleChange = (event) => {
        const input = event.target.value;
        const key = event.target.name;
        const path = `alt_permit_holders[${index}].${key}`;
        pActions.updatePendingReservation(reservationId, path, input);
    };

    const handleValidation = (key, isInvalid, error) => {
        const path = `${reservationId}_alt_permit_holders[${index}].${key}`;

        if (isInvalid) {
            pActions.formValidationChange(path, error);
        } else {
            pActions.formValidationChange(path, null);
        }
    };

    const nameValidatorMethod = (value) => {
        return isEmpty(value);
    };

    const renderRemoveLeaderButton = () => {
        if (isDisabled) {
            return <div />;
        }

        return (
            <div className="alternate-group-leader">
                <Button
                    type="button"
                    appearance="subtle-danger"
                    className="remove-leader-link"
                    onClick={onClickRemove}
                    iconBeforeElement={<Icon iconName="delete" />}
                    screenReaderTextBefore="Close"
                />
            </div>
        );
    };

    return (
        <div className="permit-alt-input-wrap m-1">
            <div className="permit-alt-header-wrap">
                <div className="permit-alt-header">
                    Alternate Permit Holder #{index + 1}
                </div>
                {renderRemoveLeaderButton()}
            </div>

            <FlexRow className="mb-1">
                <FlexCol xs={12} md={6} lg={4}>
                    <label htmlFor="other-first_name">First Name</label>
                    <input
                        id="other-first_name"
                        type="text"
                        name="first_name"
                        className={className}
                        value={get(leader, 'first_name', '')}
                        onChange={handleChange}
                        readOnly={isDisabled}
                        disabled={isDisabled}
                    />
                    <CustomValidation
                        value={get(leader, 'first_name', '')}
                        displayInSummary
                        customValidator={nameValidatorMethod}
                        onValidated={() => handleValidation('first_name')}
                        controlLabel="Alternate Permit Holder First Name"
                    />
                </FlexCol>
                <FlexCol xs={12} md={6} lg={4}>
                    <label htmlFor="other-last_name">Last Name</label>
                    <input
                        id="other-last_name"
                        type="text"
                        name="last_name"
                        className={className}
                        value={get(leader, 'last_name', '')}
                        onChange={handleChange}
                        readOnly={isDisabled}
                        disabled={isDisabled}
                    />
                    <CustomValidation
                        value={get(leader, 'last_name', '')}
                        displayInSummary
                        customValidator={nameValidatorMethod}
                        onValidated={() => handleValidation('last_name')}
                        controlLabel="Alternate Permit Holder Last Name"
                    />
                </FlexCol>
                <FlexCol xs={12} md={12} lg={4}>
                    <label htmlFor="other-phone">Phone</label>
                    <input
                        id="other-phone"
                        type="text"
                        name="cell_phone"
                        className={className}
                        value={get(leader, 'cell_phone', '')}
                        onChange={handleChange}
                        readOnly={isDisabled}
                        disabled={isDisabled}
                    />
                    <CustomValidation
                        value={get(leader, 'cell_phone', '')}
                        displayInSummary
                        customValidator={alternateLeaderValidatorMethod}
                        onValidated={() => handleValidation('cell_phone')}
                        controlLabel="Alternate Permit Holder Phone"
                    />
                </FlexCol>
            </FlexRow>
        </div>
    );
}

AlternatePermitHolderNamePhoneEntry.propTypes = propTypes;
AlternatePermitHolderNamePhoneEntry.defaultProps = defaultProps;
